// src/pages/gt3x/Protest.jsx
import { useEffect, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const debug = window.location.search.includes("debug=1");

// ❗ Apps-Script URL (wie bei Register.jsx, aber hier deine neue URL)
const SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbyRSRhDdrYfNqYS2qbnZ2s-D1M-aZdwvoJnulwyFBHhDCFazgZlhY_1sUHxO7kLh4Yqpg/exec";

const initialForm = {
    raceNumber: "",        // R1..R12
    timestamp: "",
    carNoProtester: "",
    driverProtester: "",
    carNoOther: "",
    driverOther: "",
    description: "",
};

export default function Protest() {
    const [form, setForm] = useState(initialForm);
    const [raceOptions, setRaceOptions] = useState([]); // [{id, label}]
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [showTsHint, setShowTsHint] = useState(false);

    // ------------------ Helpers ------------------
    const filledClass = (value) =>
        value && String(value).trim() !== "" ? " reg-input--filled" : "";

    const labelStyle = {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: "8px",
        fontWeight: 600,
        fontFamily:
            'var(--ff-Kallisto), "Rajdhani", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        fontSize: "0.95rem",
    };

    const fullRow = { gridColumn: "1 / -1" };

    // Hilfsfunktion wie in RaceSessionSelect
    function toInt(v) {
        const n = parseInt(String(v).trim(), 10);
        return Number.isFinite(n) ? n : null;
    }

    // ------------------ Rennen-Dropdown: Schedule auslesen ------------------
    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: "Schedule!A1:B" });

                let matrix = [];
                if (Array.isArray(res?.values)) {
                    matrix = res.values;
                } else if (Array.isArray(res?.rows)) {
                    matrix = res.rows.map((r) => Object.values(r));
                }

                if (matrix.length && toInt(matrix[0]?.[0]) === null) {
                    matrix = matrix.slice(1);
                }

                const opts = matrix
                    .map((row) => {
                        const id = toInt(row?.[0]);
                        const track = String(row?.[1] ?? "").trim();
                        if (!id) return null;
                        return { id, track, label: `R${id} – ${track}` };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.id - b.id);

                setRaceOptions(opts);

            } catch (e) {
                console.warn("Schedule load for Protest failed", e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------------------ Form-Handler ------------------
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMsg("");
        setErrorMsg("");

        try {
            if (!form.raceNumber || !form.timestamp || !form.description) {
                setErrorMsg(
                    "Bitte wähle ein Rennen und fülle mindestens Timestamp und Beschreibung aus."
                );
                setLoading(false);
                return;
            }

            const fd = new FormData();

            // Kennung für das Script, damit es in den richtigen Tab / Spalten schreibt
            fd.append("action", "protest");
            fd.append("series", "GT3X");

            Object.entries(form).forEach(([key, value]) => {
                fd.append(key, value ?? "");
            });

            const res = await fetch(SCRIPT_URL, {
                method: "POST",
                body: fd,
            });

            if (res.type === "opaque") {
                console.warn("Opaque response (CORS) – nehme Erfolg an.");
                setSuccessMsg("Dein Protest wurde übermittelt. Vielen Dank.");
                setForm((prev) => ({ ...initialForm, raceNumber: prev.raceNumber }));
                setLoading(false);
                return;
            }

            const text = await res.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error("Konnte Antwort vom Script nicht als JSON lesen.");
            }

            if (!res.ok || !json.ok) {
                throw new Error(json.error || "Unbekannter Fehler vom Apps Script.");
            }

            setSuccessMsg("Dein Protest wurde erfolgreich gespeichert. Vielen Dank.");
            setForm((prev) => ({ ...initialForm, raceNumber: prev.raceNumber }));
        } catch (err) {
            console.error("Fehler beim Protest:", err);
            setErrorMsg(
                "Der Protest konnte nicht gespeichert werden. Bitte probiere es später erneut oder kontaktiere die Orga."
            );
        } finally {
            setLoading(false);
        }
    };

    // ------------------ Render ------------------
    return (
        <div className="card card--accent">
            <h1>Protest einreichen – GT3 Xperience</h1>
            <p style={{ marginBottom: "18px" }}>
                Bevor du einen Protest schreibst, prüfe bitte im Tab Incidents, ob dein Vorfall bereits <br/>
                automatisch in die Liste der zu prüfenden Vorfälle aufgenommen wurde. <br/>
                Falls nicht, dann schreibe gerne hier deinen Protest. <br/>
                Bitte fülle alle Felder so präzise wie möglich aus. Dein Protest wird
                direkt im Rennkommissions-Sheet erfasst.
            </p>

            {debug && (
                <pre
                    style={{
                        fontSize: "12px",
                        padding: "10px",
                        background: "rgba(0,0,0,0.4)",
                        border: "1px solid #444",
                        marginBottom: "14px",
                        color: "#e4fe3c",
                        whiteSpace: "pre-wrap",
                    }}
                >
DEBUG MODE AKTIV
                    {"\n"}Script URL: {SCRIPT_URL}
                    {"\n"}Form-State: {JSON.stringify(form, null, 2)}
                </pre>
            )}

            {/* Status-Meldungen */}
            {successMsg && (
                <div
                    style={{
                        marginBottom: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(105,251,144,0.6)",
                        background: "rgba(105,251,144,0.08)",
                        color: "#69fb90",
                        fontSize: "0.96rem",
                    }}
                >
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div
                    style={{
                        marginBottom: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,44,70,0.6)",
                        background: "rgba(255,44,70,0.08)",
                        color: "#ff8c8c",
                        fontSize: "0.96rem",
                    }}
                >
                    {errorMsg}
                </div>
            )}

            <form
                onSubmit={handleSubmit}
                style={{
                    marginTop: "16px",
                    display: "grid",
                    gridTemplateColumns: "max(170px, 32%) minmax(0, 1fr)",
                    columnGap: "18px",
                    rowGap: "10px",
                    maxWidth: "780px",
                    width: "100%",
                }}
                className="reg-grid"
            >
                {/* Rennen */}
                <label htmlFor="raceNumber" style={labelStyle}>
                    Rennen
                </label>
                <select
                    id="raceNumber"
                    name="raceNumber"
                    className={`reg-input${filledClass(form.raceNumber)}`}
                    value={form.raceNumber}
                    onChange={handleChange}
                    required
                >
                    <option value="">Bitte wählen…</option>
                    {raceOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                            {o.label}
                        </option>
                    ))}
                </select>

                {/* Timestamp + Info-Icon */}
                <label htmlFor="timestamp" style={labelStyle}>
                    Timestamp{" "}
                    <button
                        type="button"
                        onClick={() => setShowTsHint((v) => !v)}
                        style={{
                            marginLeft: "6px",
                            borderRadius: "999px",
                            border: "1px solid var(--border-soft)",
                            width: "20px",
                            height: "20px",
                            fontSize: "11px",
                            lineHeight: "18px",
                            textAlign: "center",
                            padding: 0,
                            background: "transparent",
                            color: "var(--accent)",
                            cursor: "pointer",
                        }}
                        aria-label="Hinweis zum Timestamp anzeigen"
                    >
                        i
                    </button>
                </label>
                <input
                    id="timestamp"
                    placeholder="z.B. 18:22"
                    className={`reg-input${filledClass(form.timestamp)}`}
                    type="text"
                    name="timestamp"
                    value={form.timestamp}
                    onChange={handleChange}
                    required
                />

                {showTsHint && (
                    <div style={{ ...fullRow, margin: "6px 0 10px" }}>
                        <div
                            className="card"
                            style={{
                                padding: "12px 14px",
                                background: "rgba(0,0,0,0.5)",
                                borderRadius: "10px",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <p className="muted" style={{ marginBottom: "8px" }}>
                                So findest du den Timestamp im iRacing-UI / Replay:
                            </p>
                            <img
                                src="/landing/TimestampExample.png"
                                alt="Beispiel: Timestamp im iRacing UI"
                                style={{ width: "100%", maxWidth: "480px", display: "block" }}
                            />
                        </div>
                    </div>
                )}

                {/* Fahrzeugnummer Protestführer */}
                <label htmlFor="carNoProtester" style={labelStyle}>
                    Car # Protestführer
                </label>
                <input
                    id="carNoProtester"
                    className={`reg-input${filledClass(form.carNoProtester)}`}
                    type="text"
                    name="carNoProtester"
                    value={form.carNoProtester}
                    onChange={handleChange}
                />

                {/* Fahrername Protestführer */}
                <label htmlFor="driverProtester" style={labelStyle}>
                    Fahrername Protestführer
                </label>
                <input
                    id="driverProtester"
                    className={`reg-input${filledClass(form.driverProtester)}`}
                    type="text"
                    name="driverProtester"
                    value={form.driverProtester}
                    onChange={handleChange}
                />

                {/* Fahrzeugnummer beteiligtes Fahrzeug */}
                <label htmlFor="carNoOther" style={labelStyle}>
                    Car # beteiligtes Fahrzeug
                </label>
                <div style={{ display: "grid", gap: "4px" }}>
                    <input
                        id="carNoOther"
                        className={`reg-input${filledClass(form.carNoOther)}`}
                        type="text"
                        name="carNoOther"
                        value={form.carNoOther}
                        onChange={handleChange}
                    />
                    <small className="muted">
                        Falls mehrere Fahrzeuge betroffen sind, wird das von der ReKo
                        automatisch gesichtet.
                    </small>
                </div>

                {/* Fahrername beteiligtes Fahrzeug */}
                <label htmlFor="driverOther" style={labelStyle}>
                    Fahrername Beteiligter
                </label>
                <input
                    id="driverOther"
                    className={`reg-input${filledClass(form.driverOther)}`}
                    type="text"
                    name="driverOther"
                    value={form.driverOther}
                    onChange={handleChange}
                />

                {/* Beschreibung des Vorfalls */}
                <label htmlFor="description" style={labelStyle}>
                    Beschreibung des Vorfalls
                </label>
                <div style={{ display: "grid", gap: "4px" }}>
                    <textarea
                        id="description"
                        className={`reg-input${filledClass(form.description)}`}
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={6}
                        placeholder="Bitte beschreibe den Ablauf aus deiner Sicht (Session, Situation, Aktion, Folgen)…"
                        style={{ resize: "vertical" }}
                        required
                    />
                </div>

                {/* Button-Zeile */}
                <div
                    style={{
                        ...fullRow,
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "10px",
                    }}
                >
                    <button
                        className="button button--primary"
                        type="submit"
                        disabled={loading}
                        style={{
                            minWidth: "200px",
                            padding: "10px 18px",
                            fontFamily:
                                'var(--ff-Kallisto), "Rajdhani", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                            fontWeight: 700,
                            letterSpacing: ".04em",
                            textTransform: "uppercase",
                            fontSize: "0.9rem",
                        }}
                    >
                        {loading ? "Sende Protest…" : "Protest absenden"}
                    </button>
                </div>
            </form>
        </div>
    );
}
